import { TASTES } from "./PeopleSteps";
import React, { useState } from "react";
import { MapPin, LocateFixed, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Pill, Label, PillGroup, TagInput, inputCls, toggleIn } from "./Bits";
import StaplesPicker from "../StaplesPicker";
import BarcodeScanner from "../BarcodeScanner";
import KitchenScan from "../KitchenScan";
import { Camera, SkipForward } from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const COMMON_ALLERGIES = ["Nuts", "Peanuts", "Gluten", "Dairy", "Eggs", "Shellfish", "Fish", "Soy", "Sesame", "Lactose"];
const DIETS = ["Vegetarian", "Vegan", "Pescatarian", "Gluten-free", "Dairy-free", "Halal", "Kosher", "Low FODMAP", "Low carb", "Low fat", "Diabetic-friendly", "High protein"];
const CUISINES = ["Aussie classics", "Italian", "Asian", "Thai", "Indian", "Mexican", "Mediterranean", "Middle Eastern"];
const LIKES = ["Pasta", "Rice dishes", "Tacos", "Curries", "Stir fries", "Roasts", "BBQ", "Salads", "Soups", "Wraps", "Pizza", "Snags"];
const SKILLS = [
  { key: "beginner", label: "Beginner", hint: "Keep it simple" },
  { key: "confident", label: "Confident", hint: "Happy to follow a recipe" },
  { key: "adventurous", label: "Adventurous", hint: "Bring on the challenge" },
];
const APPLIANCES = ["Oven", "Stovetop", "Microwave", "Air fryer", "Slow cooker", "Pressure cooker", "Thermomix", "BBQ", "Rice cooker", "Blender", "Sandwich press", "Kettle"];

export function AllergiesStep({ form, set }) {
  return (
    <div className="space-y-6">
      <div>
        <Label hint="I'll never suggest anything with these in it.">Allergies & intolerances</Label>
        <PillGroup options={[...COMMON_ALLERGIES, ...form.allergies.filter((a) => !COMMON_ALLERGIES.includes(a))]} value={form.allergies} onChange={(v) => set("allergies", v)} testPrefix="allergy" />
        <div className="mt-3"><TagInput value={[]} onChange={(v) => set("allergies", [...form.allergies, ...v])} placeholder="Add another…" testId="custom-allergy" /></div>
      </div>
      <div>
        <Label>Dietary requirements</Label>
        <PillGroup options={DIETS} value={form.dietary_requirements} onChange={(v) => set("dietary_requirements", v)} testPrefix="diet" />
      </div>
    </div>
  );
}

export function TastesStep({ form, set }) {
  return (
    <div className="space-y-6">
      <div>
        <Label hint="How adventurous should dinners be? Anyone can have their own setting on the People step.">Household taste style</Label>
        <div className="space-y-2" data-testid="taste-style">
          {TASTES.map((t) => (
            <button key={t.key} type="button" onClick={() => set("taste_style", t.key)} data-testid={`taste-style-${t.key}`}
              className={`w-full text-left rounded-2xl border-2 px-4 py-3 transition-colors ${form.taste_style === t.key ? "bg-sorted-ink border-sorted-ink text-white" : "bg-white border-[#FDECEF] text-sorted-ink"}`}>
              <span className="block font-bold text-sm">{t.label}</span>
              <span className={`block text-xs font-semibold ${form.taste_style === t.key ? "text-white/80" : "text-sorted-slate"}`}>{t.hint}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label>What does the household love?</Label>
        <PillGroup options={LIKES} value={form.likes} onChange={(v) => set("likes", v)} testPrefix="like" />
        <div className="mt-3"><TagInput value={form.likes.filter((l) => !LIKES.includes(l))} onChange={(v) => set("likes", [...form.likes.filter((l) => LIKES.includes(l)), ...v])} placeholder="Add a like…" testId="likes-input" color="bg-sorted-sage" /></div>
      </div>
      <div>
        <Label>Cuisine loves</Label>
        <PillGroup options={CUISINES} value={form.cuisine_prefs} onChange={(v) => set("cuisine_prefs", v)} testPrefix="cuisine" />
      </div>
      <div>
        <Label hint="Anything the whole household isn't keen on.">Dislikes</Label>
        <TagInput value={form.dislikes} onChange={(v) => set("dislikes", v)} placeholder="e.g. Mushrooms, olives…" testId="dislikes-input" />
      </div>
      <div>
        <Label hint="I'll work a couple of these in each week.">Favourite meals</Label>
        <TagInput value={form.favourite_meals} onChange={(v) => set("favourite_meals", v)} placeholder="e.g. Spag bol, butter chicken…" testId="favourites-input" color="bg-sorted-sage" />
      </div>
      <div>
        <Label hint="Hard no's — these will never show up.">Foods you never want suggested</Label>
        <TagInput value={form.never_suggest} onChange={(v) => set("never_suggest", v)} placeholder="e.g. Liver, brussels sprouts…" testId="never-input" color="bg-[#FAD9CE]" />
      </div>
    </div>
  );
}

export function CookingStep({ form, set }) {
  return (
    <div className="space-y-6">
      <div>
        <Label>How confident are you in the kitchen?</Label>
        <div className="grid gap-2">
          {SKILLS.map((s) => (
            <button key={s.key} type="button" onClick={() => set("cooking_skill", s.key)} data-testid={`skill-${s.key}`}
              className={`text-left rounded-2xl border-2 px-4 py-3 transition-colors ${form.cooking_skill === s.key ? "bg-sorted-peach border-sorted-peach" : "bg-white border-[#FFF3F0]"}`}>
              <span className="font-bold text-sorted-ink block">{s.label}</span>
              <span className="text-xs text-sorted-slate">{s.hint}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label hint="I'll only suggest recipes you can actually make.">What appliances do you have?</Label>
        <PillGroup options={APPLIANCES} value={form.appliances} onChange={(v) => set("appliances", v)} testPrefix="appliance" />
      </div>
    </div>
  );
}

export function PlanningStep({ form, set }) {
  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-baseline justify-between mb-1">
          <label className="text-sm font-bold text-sorted-ink">Dinners to plan each week</label>
          <span className="font-heading text-2xl font-bold text-sorted-ink" data-testid="meals-value">{form.meals_per_week}</span>
        </div>
        <input type="range" min="1" max="7" value={form.meals_per_week} data-testid="meals-slider" onChange={(e) => set("meals_per_week", Number(e.target.value))} className="w-full accent-[#FFB5A7]" />
        <div className="flex gap-2 mt-3">
          <Pill active={form.include_lunches} onClick={() => set("include_lunches", !form.include_lunches)} testId="toggle-lunches">+ Lunches</Pill>
          <Pill active={form.include_breakfasts} onClick={() => set("include_breakfasts", !form.include_breakfasts)} testId="toggle-breakfasts">+ Brekkie</Pill>
        </div>
      </div>
      <div>
        <div className="flex items-baseline justify-between">
          <label className="text-sm font-bold text-sorted-ink">Weekly grocery budget</label>
          <span className="font-heading text-2xl font-bold text-sorted-ink" data-testid="budget-value">A${form.budget_weekly} <span className="text-xs text-sorted-slate">AUD</span></span>
        </div>
        <input type="range" min="100" max="600" step="10" value={form.budget_weekly} data-testid="budget-slider" onChange={(e) => set("budget_weekly", Number(e.target.value))} className="w-full mt-3 accent-[#FFB5A7]" />
        <div className="flex justify-between text-xs text-sorted-slate font-semibold mt-1"><span>A$100</span><span>A$600</span></div>
      </div>
      <div>
        <Label hint="I'll keep these under 20 minutes.">Busy nights</Label>
        <div className="flex flex-wrap gap-2">
          {DAYS.map((d) => <Pill key={d} active={form.busy_nights.includes(d)} onClick={() => set("busy_nights", toggleIn(form.busy_nights, d))} testId={`busy-${d}`}>{d.slice(0, 3)}</Pill>)}
        </div>
      </div>
    </div>
  );
}

export function SupermarketStep({ form, set }) {
  const [postcode, setPostcode] = useState(form.postcode || "");
  const [options, setOptions] = useState(form.supermarkets || []);
  const [loading, setLoading] = useState(false);

  const apply = (data) => {
    set("postcode", data.location.postcode || postcode);
    set("suburb", data.location.suburb || "");
    setPostcode(data.location.postcode || postcode);
    setOptions(Array.from(new Set([...data.supermarkets, ...form.supermarkets])));
    toast.success(data.location.suburb ? `Found shops near ${data.location.suburb}` : "Found your local shops");
  };

  const lookup = async () => {
    if (!postcode.trim()) return toast.error("Pop in your suburb or postcode first");
    setLoading(true);
    try { const { data } = await api.get("/supermarkets", { params: { postcode: postcode.trim() } }); apply(data); }
    catch { toast.error("Couldn't look that up — try again"); }
    finally { setLoading(false); }
  };

  const locate = () => {
    if (!navigator.geolocation) return toast.error("Location isn't available on this device");
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try { const { data } = await api.get("/supermarkets", { params: { lat: pos.coords.latitude, lng: pos.coords.longitude } }); apply(data); }
        catch { toast.error("Couldn't find shops near you"); }
        finally { setLoading(false); }
      },
      () => { setLoading(false); toast.error("Location blocked — type your postcode instead"); },
      { timeout: 10000 }
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <Label hint="So I can suggest what's actually on the shelves near you.">Where do you shop?</Label>
        <div className="flex items-center gap-2">
          <input data-testid="postcode-input" value={postcode} onChange={(e) => setPostcode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), lookup())} placeholder="Suburb or postcode" className={inputCls} />
          <button type="button" onClick={lookup} disabled={loading} data-testid="postcode-lookup" aria-label="Find shops"
            className="w-11 h-11 rounded-full bg-sorted-peach text-sorted-ink flex items-center justify-center shrink-0">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <MapPin size={18} strokeWidth={2.5} />}
          </button>
        </div>
        <button type="button" onClick={locate} disabled={loading} data-testid="use-location"
          className="mt-3 flex items-center gap-2 text-sm font-bold text-sorted-rose">
          <LocateFixed size={16} strokeWidth={2.5} /> Use my location
        </button>
        {form.suburb && <p className="text-xs text-sorted-slate mt-2" data-testid="location-label">📍 {form.suburb}{form.postcode ? `, ${form.postcode}` : ""}</p>}
      </div>
      <div>
        <Label>Preferred supermarkets</Label>
        {options.length === 0 && <p className="text-xs text-sorted-slate">Look up your area above and I'll list what's nearby.</p>}
        <PillGroup options={options} value={form.supermarkets} onChange={(v) => set("supermarkets", v)} testPrefix="shop" />
        <div className="mt-3"><TagInput value={form.supermarkets.filter((s) => !options.includes(s))} onChange={(v) => set("supermarkets", [...form.supermarkets.filter((s) => options.includes(s)), ...v])} placeholder="Add another shop…" testId="shop-input" color="bg-sorted-sage" /></div>
      </div>
    </div>
  );
}

export function KitchenStep({ form, set, onSkip, skipping }) {
  const addNames = (names) => {
    const have = new Set(form.inventory.map((i) => i.toLowerCase()));
    set("inventory", [...form.inventory, ...names.filter((n) => !have.has(n.toLowerCase()))]);
  };
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-3xl border-2 border-[#FDECEF] p-4 shadow-soft" data-testid="onboard-scan-card">
        <p className="font-heading font-semibold text-sorted-ink flex items-center gap-1.5"><Camera size={16} strokeWidth={2.5} className="text-sorted-rose" /> Quickest way: snap your kitchen</p>
        <p className="text-[11px] text-sorted-slate font-semibold mt-0.5 mb-3">Take a photo of your fridge, freezer and pantry — pick several at once if you've got two fridges or a big pantry. I'll list what I spot and you just confirm.</p>
        <KitchenScan activeId={null} onItems={addNames} />
        {onSkip && (
          <button type="button" onClick={onSkip} disabled={skipping} data-testid="onboard-skip-kitchen"
            className="mt-3 w-full text-xs font-bold text-sorted-slate py-2 flex items-center justify-center gap-1 disabled:opacity-60">
            <SkipForward size={13} strokeWidth={2.5} /> Skip this step — I'll add my kitchen later
          </button>
        )}
      </div>
      <div>
        <Label hint="Or type them, scan a barcode, or tap the staples below.">Kitchen inventory</Label>
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0"><TagInput value={form.inventory} onChange={(v) => set("inventory", v)} placeholder="e.g. Rice, eggs, pasta…" testId="inventory-input" color="bg-sorted-sage" /></div>
          <BarcodeScanner testPrefix="onboard-barcode" onFound={(name, useBy) => {
            if (!form.inventory.some((i) => i.toLowerCase() === name.toLowerCase())) set("inventory", [...form.inventory, name]);
            if (useBy) set("inventory_meta", { ...(form.inventory_meta || {}), [name.toLowerCase()]: { use_by: useBy } });
          }} />
        </div>
        <StaplesPicker value={form.inventory} onChange={(v) => set("inventory", v)} testPrefix="onboard-staples" />
      </div>
      <div>
        <Label hint="Non-food bits to add to the shop each week.">Household items</Label>
        <TagInput value={form.household_items} onChange={(v) => set("household_items", v)} placeholder="e.g. Toilet paper, dishwashing tablets…" testId="household-items-input" />
      </div>
    </div>
  );
}
